import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import useAiData, { IAiAssistData } from "./hooks/useAiData";
import { AiClient, OutputAndSource, useAiWithRag } from "../ai/contexts/ai.rag.context";
import { useTranslation } from "@me8eon/translation";
import useSearchStore from "../../store/searchStore";
import { useFeedback } from "@me8eon/feedback";
import { useAiSourceMemory } from "../ai/clients/ai.rag.client";
import { getFeedbackDataFromSource } from "./wrappers/AiMessages";
import { MessageWithActionAndData, useMakeNewSession, useMessages } from "@me8eon/sessions_messages";
import { useFeatureFlags } from "@me8eon/featureflags";
import { useUserData, useJwtToken } from "@me8eon/react_authentication";
import { extractQuestionSuggestions } from "@me8eon/messages";
import { useLoadingFlag, useQuestionSuggestions } from "@me8eon/async";
import { useAiAgentAndPipelineResults } from "@me8eon/react_intent";
import { QuickActionType } from "../../modules/AiWidgets/widget.types";

export type AssistantTask = {
    requestId: string;
    task: string;
    status: string;
    timestamp?: number;
}



export interface IncidentFileAttachmentType {
    file_name: string;
    size: number;
    file_data: string;
}

interface IContextValues {
    handleChangeCurrentCardKey: (key: string) => void;
    currentCardKey: string;
    handleAiQuery: (query: string, action?: string, data?: any, condition?: (context: any) => any) => void;
    messages: MessageWithActionAndData[];
    actualMessages: MessageWithActionAndData[];
    isLoading: boolean;
    setIsLoading: (value: boolean) => void;
    cards: IAiAssistData;
    handleResetChat: () => void;
    setMessages?: (messages: MessageWithActionAndData[]) => void;
    incidentText: string;
    setIncidentText: (value: string) => void;
    contactNumber: string;
    setContact: (value: string) => void;
    incidentFiles: IncidentFileAttachmentType[];
    setIncidentFiles: (files: IncidentFileAttachmentType[]) => void;
    isIncidentTextPending: boolean;
    setIsIncidentTextPending: (value: boolean) => void;
    isITIssue: boolean;
    quickActionType: QuickActionType;
    setQuickActionType: (actionType: QuickActionType) => void;
    questionSuggestions: string[];
    setQuestionSuggestions: (suggestions: string[]) => void;
    messagePassedFromWidget: string;
    setMessagePassedFromWidget: (message: string) => void;
    isChatHistory: boolean;
    setIsChatHistory?: (value: boolean) => void;
    currentAgent: string;
    setCurrentAgent: (value: string) => void;
}

const AiAssistContext = createContext<IContextValues>({
    handleChangeCurrentCardKey: () => {
    },
    currentCardKey: "",
    messages: [],
    actualMessages: [],
    handleAiQuery: () => {
    },
    isLoading: false,
    setIsLoading: (value: boolean) => {
    },
    cards: {},
    handleResetChat: () => {
    },
    setMessages: () => {
    },
    incidentText: "",
    setIncidentText: () => {
    },
    contactNumber: "",
    setContact: () => { },
    incidentFiles: [],
    setIncidentFiles: () => {
    },
    isIncidentTextPending: true,
    setIsIncidentTextPending: () => {

    },
    isITIssue: false,
    quickActionType: undefined,
    setQuickActionType: () => {

    },
    questionSuggestions: [],
    setQuestionSuggestions: () => { },
    messagePassedFromWidget: "",
    setMessagePassedFromWidget: () => { },
    isChatHistory: false,
    setIsChatHistory: () => { },
    currentAgent: "",
    setCurrentAgent: () => { }
});

const featureFlagBasedMessageStore = () => {
    return useMessages();
}

const AiAssistProvider = ({ children }: { children: ReactNode }) => {
    const cards: IAiAssistData = useAiData();
    const userData = useUserData();
    const [messages = [], setMessages] = featureFlagBasedMessageStore();
    const newSession = useMakeNewSession();
    const jwtTokenFn = useJwtToken();
    const actualMessages = useMemo(() => (messages || []).filter((msg) => msg.action !== "quick-actions"), [messages]);
    const [isLoading, setIsLoading] = useLoadingFlag();
    const [currentCardKey, setCurrentCardKey] = useState("selfService");
    const [incidentText, setIncidentText] = useState<string>("");
    const { contactNumber: contact } = userData;
    const [contactNumber, setContact] = useState<string>(contact);
    const [incidentFiles, setIncidentFiles] = useState<IncidentFileAttachmentType[]>([]);
    const [isIncidentTextPending, setIsIncidentTextPending] = useState(true);
    const [isITIssue, setIsITIssue] = useState<boolean>(false);
    const [questionSuggestions, setQuestionSuggestions] = useQuestionSuggestions()
    const [assistanceTasks, setAssistanceTasks] = useState(() => {
        return JSON.parse(localStorage.getItem("latestTasks") || "[]");
    });
    const [manualRecommendedActions, setManualRecommendedActions] = useState<string[]>(() => {
        return JSON.parse(localStorage.getItem("manualRecommendedActions") || "[]");
    });
    const [quickActionType, setQuickActionType] = useState<QuickActionType>(undefined);
    const [messagePassedFromWidget, setMessagePassedFromWidget] = useState<string>("");
    const { language } = useTranslation().i18n;
    const ai: AiClient = useAiWithRag();
    const { setShowLiveAgentChat } = useSearchStore();
    const feedback = useFeedback();
    const [rememberedSources] = useAiSourceMemory();
    const [ff] = useFeatureFlags();
    const prevMessagesLength = useRef(0);
    const [isChatHistory, setIsChatHistory] = useState<boolean>(true);

    const [currentAgent, setCurrentAgent] = useState<string>("");
    const [agentAndPipelineResults] = useAiAgentAndPipelineResults();
    const selectedAgent = agentAndPipelineResults.agentResult?.value?.selected;

    const getQuery = (msgArray: MessageWithActionAndData[]) => {
        const lastMessage = msgArray[messages.length - 1];
        return lastMessage.content;
    };

    useEffect(() => {
        setCurrentAgent(selectedAgent);
    }, [selectedAgent]);

    useEffect(() => {
        if (messages.length === 0) return;
        const query = getQuery(messages);
        feedback({
            source: "aiassist-query",
            sources: rememberedSources.map(getFeedbackDataFromSource),
            messages,
            query: query as string,
            role: userData.currentRole || "unknown",
            company: userData.company || "unknown",
        });
    }, [rememberedSources]);

    const handleChangeCurrentCardKey = (key: string) => {
        setCurrentCardKey(key);
    }

    const handleAiQuery = useCallback((query: string, action: string = "user-query", data: any, condition: ((context: any) => any) | null = null) => {
        if (query.trim().length > 0) {
            setIsChatHistory(false);
            setMessages(old => [...old, {
                role: "user",
                action,
                content: query,
                data,
                condition
            }]);
            setIsLoading(true);
        }
    }
        , [cards, currentCardKey, messages]);

    const handleAiAssist = useCallback(async (query: string, action: string, data: any) => {
        const messagesWithoutLast = actualMessages.slice(0, messages.length - 1);
        return await cards[currentCardKey].handleAiQuery(messagesWithoutLast, query, action, quickActionType, data, ff);
    }
        , [cards, currentCardKey, messages]);

    const addAssistantTask = (task: AssistantTask) => {
        let localTasks: AssistantTask[] = JSON.parse(localStorage.getItem("latestTasks") || "[]");
        const taskMap = new Map(localTasks.map(item => [item.requestId, item]));
        const existingTask = taskMap.get(task.requestId);
        if (existingTask) {
            // Merge only changed fields
            const mergedTask = { ...existingTask, ...task };
            taskMap.set(task.requestId, mergedTask);
        } else {
            taskMap.set(task.requestId, task);
        }
        const updatedTasks = Array.from(taskMap.values());
        localStorage.setItem("latestTasks", JSON.stringify(updatedTasks));
        setAssistanceTasks(updatedTasks);
    };

    const removeOldAssistanceTask = useCallback(() => {
        try {
            const storedTasks: AssistantTask[] = JSON.parse(localStorage.getItem("latestTasks") || "[]");
            const now = Date.now();

            const updatedTasks = storedTasks.filter(task => {
                if (task.timestamp) {
                    const taskTime = new Date(task.timestamp).getTime();
                    const diffInMinutes = Math.abs(now - taskTime) / 1000 / 60;
                    return diffInMinutes < 20; // Keep tasks from the last 20 minutes
                }
                return false;
            });

            localStorage.setItem("latestTasks", JSON.stringify(updatedTasks));
            setAssistanceTasks(updatedTasks);
        } catch (error) {
            console.error("Failed to clean up tasks from localStorage:", error);
        }
    }, []);


    useEffect(() => {
        if (messages.length === 0) return;
        return;


        const lastMessage = messages[messages.length - 1];
        const role = lastMessage.role;
        const action = lastMessage.action;
        const data = lastMessage.data;

        const getResponse = async () => {
            const query = getQuery(messages);
            const response: OutputAndSource = await handleAiAssist(query, action || "", data);
            const { content, suggestions } = extractQuestionSuggestions(response.output);
            setMessages([...messages, {
                role: "assistant",
                content,
                action: response?.action,
                data: response?.data,
            }]);
            setQuestionSuggestions(suggestions);
            setIsLoading(false);
        };
        if (role !== "assistant") {
            getResponse().then(r => r);
        }
        setIsChatHistory(checkIfChatHistory(messages));
    }, [messages]);

    useEffect(() => {
        return;
        if (actualMessages.length === 0) return;

        const lastMessage = actualMessages[actualMessages.length - 1];
        const role = lastMessage.role;

        const getIncidentText = async () => {
            const response = await ai.handleSummariseChatHistoryToIncident(
                actualMessages,
                "",
                language,
            );
            setIncidentText(response.output);
            setIsIncidentTextPending(false);
        };

        if (role === "assistant") {
            getIncidentText();
        }
    }, [actualMessages]);

    const checkIfChatHistory = (msgs: MessageWithActionAndData[]) => {
        const isHistory = (msgs.length - prevMessagesLength.current == 1) ? false : true;
        prevMessagesLength.current = msgs.length;
        return isHistory;
    };

    const handleResetChat = useCallback(() => {
        newSession("")
        setShowLiveAgentChat(false);
        setQuickActionType(undefined);
    }, [setMessages]);

    return (
        <AiAssistContext.Provider
            value={{
                handleChangeCurrentCardKey,
                currentCardKey,
                handleAiQuery,
                messages,
                actualMessages,
                setMessages,
                isLoading,
                setIsLoading,
                cards,
                handleResetChat,
                incidentText,
                setIncidentText,
                contactNumber,
                setContact,
                incidentFiles,
                setIncidentFiles,
                isIncidentTextPending,
                setIsIncidentTextPending,
                isITIssue,
                quickActionType,
                setQuickActionType,
                questionSuggestions,
                setQuestionSuggestions,
                messagePassedFromWidget,
                setMessagePassedFromWidget,
                isChatHistory,
                setIsChatHistory,
                currentAgent,
                setCurrentAgent
            }}>
            {children}
        </AiAssistContext.Provider>
    );
};

const useAiAssistContext = () => {
    const context = React.useContext(AiAssistContext);
    if (!context) {
        throw new Error("useAiAssistContext must be used within an AiAssistProvider");
    }
    return context;
}

export { AiAssistContext, AiAssistProvider, useAiAssistContext };